import React from 'react';

import NumberInputFormField from '@components/form-fields/NumberInputFormField';
import TextInputFormField from '@components/form-fields/TextInputFormField';
import WidgetPaper from '@components/widgets/display-widget/WidgetPaper';
import WidgetSection from '@components/widgets/display-widget/WidgetSection';

import { FormValues } from '../types';

import { useFormikContext } from 'formik';
import { makeStyles } from 'tss-react/mui';

const useStyles = makeStyles()(() => ({
  fullFieldRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: '16px',
    width: '100%',
    '& > div:last-child:nth-child(4n-3)': {
      gridColumn: 'span 4'
    },
    '& > div:last-child:nth-child(4n-2)': {
      gridColumn: 'span 2'
    },
    '& > div:last-child:nth-child(4n-1)': {
      gridColumn: 'span 1'
    }
  }
}));

type Props = {
  amendmentId: string;
  disabledForm: boolean;
};

const MarineHullWidgetAtom: React.FC<Props> = ({
  amendmentId,
  disabledForm
}) => {
  const { classes } = useStyles();

  const { values, errors, touched, setFieldValue, setFieldError, handleBlur } =
    useFormikContext<FormValues>();

  const marineHullDetails = values.marineHullDetails;

  const validateLength = (val: any) => {
    if (val === '' || val === null || typeof val === 'undefined') return;
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) {
      setFieldError('marineHullDetails.length', 'Length cannot be negative');
    }
  };

  const validateWeight = (val: any) => {
    if (val === '' || val === null || typeof val === 'undefined') return;
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) {
      setFieldError('marineHullDetails.weight', 'Weight cannot be negative');
    }
  };

  const validateHeight = (val: any) => {
    if (val === '' || val === null || typeof val === 'undefined') return;
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) {
      setFieldError('marineHullDetails.height', 'Height cannot be negative');
    }
  };

  const validateEngineHorsePower = (val: any) => {
    if (val === '' || val === null || typeof val === 'undefined') return;
    const n = Number(val);
    if (!Number.isInteger(n) || n < 0) {
      setFieldError(
        'marineHullDetails.engineHorsePower',
        'Invalid Engine Horse Power Value'
      );
    }
  };

  const validateEngineMaxDesignedSpeed = (val: any) => {
    if (val === '' || val === null || typeof val === 'undefined') return;
    const n = Number(val);
    if (!Number.isFinite(n) || n < 0) {
      setFieldError(
        'marineHullDetails.engineMaxDesignedSpeed',
        'Invalid Engine Max Designed Speed Value'
      );
    }
  };

  return (
    <WidgetPaper>
      <WidgetSection title="Marine Hull Details">
        {/* Section 1: Vessel Info */}
        <div className={classes.fullFieldRow}>
          <TextInputFormField
            name="marineHullDetails.vesselName"
            title="Vessel Name*"
            placeholder="Vessel Name"
            value={marineHullDetails.vesselName}
            error={
              touched?.marineHullDetails?.vesselName
                ? (errors as any)?.marineHullDetails?.vesselName
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('marineHullDetails.vesselName', e.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineHullDetails.connectionPort"
            title="Connection Port*"
            placeholder="Connection Port"
            value={marineHullDetails.connectionPort}
            error={
              touched?.marineHullDetails?.connectionPort
                ? (errors as any)?.marineHullDetails?.connectionPort
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('marineHullDetails.connectionPort', e.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineHullDetails.territorialWaters"
            title="Territorial Waters*"
            placeholder="Territorial Waters"
            value={marineHullDetails.territorialWaters}
            error={
              touched?.marineHullDetails?.territorialWaters
                ? (errors as any)?.marineHullDetails?.territorialWaters
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue(
                'marineHullDetails.territorialWaters',
                v.target.value
              )
            }
            disabled={disabledForm}
            material
          />
          <div />
        </div>
      </WidgetSection>

      <WidgetSection title="Body Details">
        <div className={classes.fullFieldRow}>
          <TextInputFormField
            name="marineHullDetails.usage"
            title="Usage*"
            placeholder="Usage"
            value={marineHullDetails.usage}
            error={
              touched?.marineHullDetails?.usage
                ? (errors as any)?.marineHullDetails?.usage
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineHullDetails.usage', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineHullDetails.builder"
            title="Builder*"
            placeholder="Builder"
            value={marineHullDetails.builder}
            error={
              touched?.marineHullDetails?.builder
                ? (errors as any)?.marineHullDetails?.builder
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineHullDetails.builder', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineHullDetails.constructionYear"
            title="Construction Year*"
            placeholder="Construction Year"
            value={marineHullDetails.constructionYear.toString()}
            error={
              touched?.marineHullDetails?.constructionYear
                ? (errors as any)?.marineHullDetails?.constructionYear
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue(
                'marineHullDetails.constructionYear',
                v.target.value
              )
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineHullDetails.registryPort"
            title="Registery Number & Port*"
            placeholder="Registery Number & Port"
            value={marineHullDetails.registryPort}
            error={
              touched?.marineHullDetails?.registryPort
                ? (errors as any)?.marineHullDetails?.registryPort
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(e) =>
              setFieldValue('marineHullDetails.registryPort', e.target.value)
            }
            disabled={disabledForm}
            material
          />
        </div>

        <div className={classes.fullFieldRow}>
          <NumberInputFormField
            name="marineHullDetails.length"
            title="Length"
            placeholder="Length"
            value={marineHullDetails.length}
            error={
              touched?.marineHullDetails?.length
                ? (errors as any)?.marineHullDetails?.length
                : ''
            }
            onBlur={(e) => {
              handleBlur(e);
              validateLength((e?.target as any)?.value);
            }}
            onChange={(v) =>
              setFieldValue('marineHullDetails.length', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <NumberInputFormField
            name="marineHullDetails.weight"
            title="Weight"
            placeholder="Weight"
            value={marineHullDetails.weight}
            error={
              touched?.marineHullDetails?.weight
                ? (errors as any)?.marineHullDetails?.weight
                : ''
            }
            onBlur={(e) => {
              handleBlur(e);
              validateWeight((e?.target as any)?.value);
            }}
            onChange={(v) =>
              setFieldValue('marineHullDetails.weight', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <NumberInputFormField
            name="marineHullDetails.height"
            title="Height"
            placeholder="Height"
            value={marineHullDetails.height}
            error={
              touched?.marineHullDetails?.height
                ? (errors as any)?.marineHullDetails?.height
                : ''
            }
            onBlur={(e) => {
              handleBlur(e);
              validateHeight((e?.target as any)?.value);
            }}
            onChange={(v) =>
              setFieldValue('marineHullDetails.height', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <div />
        </div>
      </WidgetSection>

      <WidgetSection title="Engine Details">
        <div className={classes.fullFieldRow}>
          <TextInputFormField
            name="marineHullDetails.engineType"
            title="Type*"
            placeholder="Engine Type"
            value={marineHullDetails.engineType}
            error={
              touched?.marineHullDetails?.engineType
                ? (errors as any)?.marineHullDetails?.engineType
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineHullDetails.engineType', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineHullDetails.engineNumber"
            title="Engine Number*"
            placeholder="Engine Number"
            value={marineHullDetails.engineNumber}
            error={
              touched?.marineHullDetails?.engineNumber
                ? (errors as any)?.marineHullDetails?.engineNumber
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineHullDetails.engineNumber', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineHullDetails.engineYearBuilt"
            title="Year Built*"
            placeholder="Year Built"
            value={marineHullDetails.engineYearBuilt.toString()}
            error={
              touched?.marineHullDetails?.engineYearBuilt
                ? (errors as any)?.marineHullDetails?.engineYearBuilt
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineHullDetails.engineYearBuilt', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <NumberInputFormField
            name="marineHullDetails.engineHorsePower"
            title="Horse Power*"
            placeholder="Horse Power"
            value={marineHullDetails.engineHorsePower}
            error={
              touched?.marineHullDetails?.engineHorsePower
                ? (errors as any)?.marineHullDetails?.engineHorsePower
                : ''
            }
            onBlur={(e) => {
              handleBlur(e);
              validateEngineHorsePower((e?.target as any)?.value);
            }}
            onChange={(v) =>
              setFieldValue(
                'marineHullDetails.engineHorsePower',
                v.target.value
              )
            }
            maxDecimalPrecision={0}
            disabled={disabledForm}
            material
          />
        </div>

        <div className={classes.fullFieldRow}>
          <NumberInputFormField
            name="marineHullDetails.engineMaxDesignedSpeed"
            title="Max. Designed Speed"
            placeholder="Max. Designed Speed"
            value={marineHullDetails.engineMaxDesignedSpeed}
            error={
              touched?.marineHullDetails?.engineMaxDesignedSpeed
                ? (errors as any)?.marineHullDetails?.engineMaxDesignedSpeed
                : ''
            }
            onBlur={(e) => {
              handleBlur(e);
              validateEngineMaxDesignedSpeed((e?.target as any)?.value);
            }}
            onChange={(v) =>
              setFieldValue(
                'marineHullDetails.engineMaxDesignedSpeed',
                v.target.value
              )
            }
            disabled={disabledForm}
            material
          />
          <TextInputFormField
            name="marineHullDetails.engineFuel"
            title="Fuel"
            placeholder="Fuel"
            value={marineHullDetails.engineFuel}
            error={
              touched?.marineHullDetails?.engineFuel
                ? (errors as any)?.marineHullDetails?.engineFuel
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineHullDetails.engineFuel', v.target.value)
            }
            disabled={disabledForm}
            material
          />
          <div />
          <div />
        </div>

        <div className={classes.fullFieldRow}>
          <TextInputFormField
            name="marineHullDetails.remarks"
            title="Remarks"
            placeholder="Remarks"
            value={marineHullDetails.remarks}
            error={
              touched?.marineHullDetails?.remarks
                ? (errors as any)?.marineHullDetails?.remarks
                : ''
            }
            onBlur={(e) => handleBlur(e)}
            onChange={(v) =>
              setFieldValue('marineHullDetails.remarks', v.target.value)
            }
            disabled={disabledForm}
            material
            fullWidth
          />
        </div>
      </WidgetSection>
    </WidgetPaper>
  );
};

export { MarineHullWidgetAtom };
